import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const SUBSCRIPTION_LIMITS = { retail: 1, pro: 3, advisor: 10 };

const STATUS_META = {
  active:    { label: "Active",    color: "#28a745", bg: "#d4edda" },
  inactive:  { label: "Inactive",  color: "#6c757d", bg: "#e2e3e5" },
  suspended: { label: "Suspended", color: "#dc3545", bg: "#fde2e2" },
};

const TYPE_META = {
  user:       { label: "User",       color: "#495057", bg: "#e9ecef" },
  admin:      { label: "Admin",      color: "#007bff", bg: "#cce5ff" },
  superadmin: { label: "Super Admin", color: "#6f42c1", bg: "#e8d5ff" },
};

const PLAN_META = {
  retail:  { label: "Retail",  color: "#fd7e14", bg: "#fff3cd" },
  pro:     { label: "Pro",     color: "#007bff", bg: "#cce5ff" },
  advisor: { label: "Advisor", color: "#28a745", bg: "#d4edda" },
};

const EMPTY_USER = {
  username: "", password: "", firstname: "", lastname: "",
  user_type: "user", subscription_plan: "retail", status: "active",
  validity_date: "",
};

const EMPTY_ACCOUNT = { username: "", password: "", broker: "angelone", api_key: "", totp_token: "" };

export default function AdminPage() {
  const { user: sessionUser } = useAuth();

  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState(null);
  const [search, setSearch]     = useState("");
  const [filterType, setFilterType]   = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterPlan, setFilterPlan]   = useState("ALL");

  // Add/Edit user modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser]     = useState(null); // null = adding new
  const [userDraft, setUserDraft]         = useState(EMPTY_USER);
  const [userSaving, setUserSaving]       = useState(false);

  // Accounts sub-panel
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userAccounts, setUserAccounts]     = useState({});  // {userId: [...]}
  const [showAddAcct, setShowAddAcct]       = useState(null); // userId
  const [acctDraft, setAcctDraft]           = useState(EMPTY_ACCOUNT);
  const [acctSaving, setAcctSaving]         = useState(false);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3500);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/users");
      setUsers(res.data);
    } catch {
      flash("Failed to load users.", false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchUserAccounts = async (userId) => {
    try {
      const res = await axios.get(`/admin/users/${userId}`);
      setUserAccounts(prev => ({ ...prev, [userId]: res.data.accounts || [] }));
    } catch {
      flash("Failed to load accounts.", false);
    }
  };

  const toggleExpand = (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
    } else {
      setExpandedUserId(userId);
      if (!userAccounts[userId]) fetchUserAccounts(userId);
    }
  };

  // ── User modal ──

  const openAddUser = () => {
    setEditingUser(null);
    setUserDraft(EMPTY_USER);
    setShowUserModal(true);
  };

  const openEditUser = (u, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setEditingUser({ ...u });
    setUserDraft({
      username:          u.username,
      password:          "",
      firstname:         u.firstname || "",
      lastname:          u.lastname  || "",
      user_type:         u.user_type,
      subscription_plan: u.subscription_plan,
      status:            u.status,
      validity_date:     u.validity_date || "",
    });
    setShowUserModal(true);
  };

  const saveUser = async () => {
    setUserSaving(true);
    try {
      if (editingUser) {
        const payload = { ...userDraft };
        if (!payload.password) delete payload.password; // don't update pw if blank
        delete payload.username;
        // Send null for empty validity_date — empty string is rejected by MySQL strict mode
        if (payload.validity_date === "") payload.validity_date = null;
        await axios.put(`/admin/users/${editingUser.id}`, payload);
        flash("User updated.");
        setShowUserModal(false);
        fetchUsers();
      } else {
        const payload = { ...userDraft };
        if (payload.validity_date === "") payload.validity_date = null;
        await axios.post("/admin/users", payload);
        flash("User created.");
        setShowUserModal(false);
        fetchUsers();
      }
    } catch (e) {
      flash(e?.response?.data?.error || "Save failed.", false);
    } finally {
      setUserSaving(false);
    }
  };

  // ── Quick status toggle ──

  const setStatus = async (userId, status, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    try {
      await axios.put(`/admin/users/${userId}`, { status });
      await fetchUsers();
    } catch {
      flash("Failed to update status.", false);
    }
  };

  // ── Broker accounts ──

  const addAccount = async (userId) => {
    if (!acctDraft.username || !acctDraft.password || !acctDraft.broker) {
      flash("Username, password and broker are required.", false);
      return;
    }
    setAcctSaving(true);
    try {
      await axios.post(`/admin/users/${userId}/accounts`, acctDraft);
      flash("Account added.");
      setShowAddAcct(null);
      setAcctDraft(EMPTY_ACCOUNT);
      fetchUserAccounts(userId);
      fetchUsers(); // refresh account count
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to add account.", false);
    } finally {
      setAcctSaving(false);
    }
  };

  const deleteAccount = async (userId, accountId) => {
    if (!window.confirm("Remove this broker account?")) return;
    try {
      await axios.delete(`/admin/users/${userId}/accounts/${accountId}`);
      flash("Account removed.");
      fetchUserAccounts(userId);
      fetchUsers();
    } catch {
      flash("Failed to remove account.", false);
    }
  };

  // ── Filter ──

  const displayed = users.filter(u => {
    if (filterType !== "ALL" && u.user_type !== filterType) return false;
    if (filterStatus !== "ALL" && u.status !== filterStatus) return false;
    if (filterPlan !== "ALL" && u.subscription_plan !== filterPlan) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.username.toLowerCase().includes(q) &&
          !(u.firstname || "").toLowerCase().includes(q) &&
          !(u.lastname  || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total:     users.length,
    active:    users.filter(u => u.status === "active").length,
    suspended: users.filter(u => u.status === "suspended").length,
    admins:    users.filter(u => u.user_type !== "user").length,
  };

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={pageTitle}>User Management</h2>
          <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
            Manage platform users, subscriptions and broker accounts
          </p>
        </div>
        <button type="button" onClick={openAddUser} style={btnPrimary}>+ Add User</button>
      </div>

      {/* Stats */}
      <div style={statsGrid}>
        <StatTile label="Total Users"  value={stats.total}     color="#2c3e50" />
        <StatTile label="Active"       value={stats.active}    color="#28a745" />
        <StatTile label="Suspended"    value={stats.suspended} color="#dc3545" />
        <StatTile label="Admin / Super" value={stats.admins}   color="#6f42c1" />
      </div>

      {/* Filters */}
      <div style={filterRow}>
        <input
          placeholder="Search username or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
          style={searchInput} />
        <FilterSelect label="Type"   value={filterType}   onChange={setFilterType}
          options={[["ALL","All Types"],["user","User"],["admin","Admin"],["superadmin","Super Admin"]]} />
        <FilterSelect label="Status" value={filterStatus} onChange={setFilterStatus}
          options={[["ALL","All Status"],["active","Active"],["inactive","Inactive"],["suspended","Suspended"]]} />
        <FilterSelect label="Plan"   value={filterPlan}   onChange={setFilterPlan}
          options={[["ALL","All Plans"],["retail","Retail"],["pro","Pro"],["advisor","Advisor"]]} />
        <button type="button" onClick={fetchUsers} style={btnSecondary} disabled={loading}>
          {loading ? "…" : "Refresh"}
        </button>
      </div>

      {/* Table */}
      <div style={tableCard}>
        <table style={table}>
          <thead>
            <tr>
              {["Username", "Name", "Type", "Plan", "Accounts", "Status", "Valid Until", "Actions"].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 30, color: "#888" }}>
                {loading ? "Loading…" : "No users found"}
              </td></tr>
            ) : displayed.map(u => {
              const tm = TYPE_META[u.user_type] || TYPE_META.user;
              const pm = PLAN_META[u.subscription_plan] || PLAN_META.retail;
              const sm = STATUS_META[u.status] || STATUS_META.active;
              const limit = SUBSCRIPTION_LIMITS[u.subscription_plan] || 1;
              const isExpanded = expandedUserId === u.id;
              const isSelf = sessionUser?.id === u.id;
              const isEditing = editingUser?.id === u.id;

              return (
                <React.Fragment key={u.id}>
                  <tr style={{ background: isEditing ? "#fff8e1" : isExpanded ? "#f0f7ff" : "#fff", outline: isEditing ? "2px solid #fd7e14" : "none", outlineOffset: -2, transition: "background .15s" }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{u.username}</div>
                      {isSelf && <div style={{ fontSize: 10, color: "#007bff" }}>You</div>}
                    </td>
                    <td style={td}>
                      <div style={{ fontSize: 13 }}>{[u.firstname, u.lastname].filter(Boolean).join(" ") || "—"}</div>
                    </td>
                    <td style={td}>
                      <span style={{ ...badge, background: tm.bg, color: tm.color }}>{tm.label}</span>
                    </td>
                    <td style={td}>
                      <span style={{ ...badge, background: pm.bg, color: pm.color }}>{pm.label}</span>
                      <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>max {limit} acct{limit > 1 ? "s" : ""}</div>
                    </td>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleExpand(u.id); }}
                        style={{ ...acctCountBtn, background: u.account_count > 0 ? "#e8f4fd" : "#f8f9fa", color: u.account_count > 0 ? "#007bff" : "#999" }}>
                        {u.account_count} / {limit} {isExpanded ? "▲" : "▼"}
                      </button>
                    </td>
                    <td style={td}>
                      <span style={{ ...badge, background: sm.bg, color: sm.color }}>{sm.label}</span>
                    </td>
                    <td style={td}>
                      <div style={{ fontSize: 12, color: "#555" }}>{u.validity_date || "—"}</div>
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                        <button type="button" onClick={(e) => openEditUser(u, e)} style={btnSmall}>Edit</button>
                        {u.status !== "active" && (
                          <button type="button" onClick={(e) => setStatus(u.id, "active", e)} style={{ ...btnSmall, color: "#28a745", borderColor: "#28a745" }}>Activate</button>
                        )}
                        {u.status === "active" && !isSelf && (
                          <button type="button" onClick={(e) => setStatus(u.id, "suspended", e)} style={{ ...btnSmall, color: "#dc3545", borderColor: "#dc3545" }}>Suspend</button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded broker accounts */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} style={{ padding: "0 16px 16px 40px", background: "#f0f7ff" }}>
                        <AccountsPanel
                          userId={u.id}
                          accounts={userAccounts[u.id] || []}
                          limit={limit}
                          showAdd={showAddAcct === u.id}
                          onShowAdd={() => { setShowAddAcct(u.id); setAcctDraft(EMPTY_ACCOUNT); }}
                          onHideAdd={() => setShowAddAcct(null)}
                          acctDraft={acctDraft}
                          setAcctDraft={setAcctDraft}
                          onAdd={() => addAccount(u.id)}
                          onDelete={(acctId) => deleteAccount(u.id, acctId)}
                          saving={acctSaving}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit user modal */}
      {showUserModal && (
        <Modal
          title={editingUser ? `Edit: ${editingUser.username}` : "Add New User"}
          onClose={() => setShowUserModal(false)}>
          <UserForm
            draft={userDraft}
            setDraft={setUserDraft}
            isEdit={!!editingUser}
            saving={userSaving}
            onSave={saveUser}
            onCancel={() => setShowUserModal(false)}
            sessionUser={sessionUser}
          />
        </Modal>
      )}
    </div>
  );
}

/* ── Broker accounts panel ───────────────────────────────────── */

function AccountsPanel({ userId, accounts, limit, showAdd, onShowAdd, onHideAdd, acctDraft, setAcctDraft, onAdd, onDelete, saving }) {
  return (
    <div style={{ paddingTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#2c3e50", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
        Broker Accounts ({accounts.length} / {limit})
      </div>
      {accounts.length === 0 ? (
        <div style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>No broker accounts linked.</div>
      ) : (
        <table style={{ ...table, marginBottom: 10 }}>
          <thead>
            <tr>
              {["Account Username", "Broker", "API Key", "TOTP", ""].map(h => (
                <th key={h} style={{ ...th, fontSize: 11, padding: "6px 10px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id}>
                <td style={{ ...td, padding: "6px 10px", fontWeight: 600, fontSize: 12 }}>{a.username}</td>
                <td style={{ ...td, padding: "6px 10px" }}>
                  <span style={{ ...badge, fontSize: 11 }}>{a.broker}</span>
                </td>
                <td style={{ ...td, padding: "6px 10px", fontFamily: "monospace", fontSize: 11 }}>
                  {a.api_key ? a.api_key.slice(0, 6) + "…" : "—"}
                </td>
                <td style={{ ...td, padding: "6px 10px", fontSize: 11 }}>
                  {a.totp_token ? "••••" : "—"}
                </td>
                <td style={{ ...td, padding: "6px 10px" }}>
                  <button type="button" onClick={() => onDelete(a.id)} style={{ ...btnSmall, color: "#dc3545", borderColor: "#dc3545", fontSize: 11 }}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!showAdd ? (
        accounts.length < limit ? (
          <button type="button" onClick={onShowAdd} style={btnAddAcct}>+ Add Broker Account</button>
        ) : (
          <div style={{ fontSize: 11, color: "#999" }}>Limit reached for this subscription plan.</div>
        )
      ) : (
        <div style={addAcctForm}>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: "#2c3e50" }}>New Broker Account</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
            <LabeledInput label="Account Username *" value={acctDraft.username} onChange={v => setAcctDraft(d => ({ ...d, username: v }))} />
            <LabeledInput label="Password *" type="password" value={acctDraft.password} onChange={v => setAcctDraft(d => ({ ...d, password: v }))} />
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", marginBottom: 3 }}>Broker *</div>
              <select value={acctDraft.broker} onChange={e => setAcctDraft(d => ({ ...d, broker: e.target.value }))} style={smallInput}>
                {["angelone", "zerodha", "upstox", "other"].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <LabeledInput label="API Key" mono value={acctDraft.api_key} onChange={v => setAcctDraft(d => ({ ...d, api_key: v }))} />
            <LabeledInput label="TOTP Token" mono value={acctDraft.totp_token} onChange={v => setAcctDraft(d => ({ ...d, totp_token: v }))} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onAdd} disabled={saving} style={{ ...btnPrimary, fontSize: 12, padding: "5px 14px" }}>
              {saving ? "…" : "Add Account"}
            </button>
            <button type="button" onClick={onHideAdd} style={{ ...btnSecondary, fontSize: 12, padding: "5px 14px" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── User form ───────────────────────────────────────────────── */

function UserForm({ draft, setDraft, isEdit, saving, onSave, onCancel, sessionUser }) {
  const isSuperAdmin = sessionUser?.user_type === "superadmin";

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <LabeledInput label="Username *" value={draft.username} onChange={v => setDraft(d => ({ ...d, username: v }))} disabled={isEdit} />
        <LabeledInput label={isEdit ? "New Password (leave blank to keep)" : "Password *"} type="password" value={draft.password} onChange={v => setDraft(d => ({ ...d, password: v }))} />
        <LabeledInput label="First Name" value={draft.firstname} onChange={v => setDraft(d => ({ ...d, firstname: v }))} />
        <LabeledInput label="Last Name" value={draft.lastname} onChange={v => setDraft(d => ({ ...d, lastname: v }))} />

        <div>
          <div style={formLabel}>User Type</div>
          <select value={draft.user_type} onChange={e => setDraft(d => ({ ...d, user_type: e.target.value }))} style={formInput}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
            {isSuperAdmin && <option value="superadmin">Super Admin</option>}
          </select>
        </div>

        <div>
          <div style={formLabel}>Subscription Plan</div>
          <select value={draft.subscription_plan} onChange={e => setDraft(d => ({ ...d, subscription_plan: e.target.value }))} style={formInput}>
            <option value="retail">Retail (1 account)</option>
            <option value="pro">Pro (3 accounts)</option>
            <option value="advisor">Advisor (10 accounts)</option>
          </select>
        </div>

        <div>
          <div style={formLabel}>Status</div>
          <select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))} style={formInput}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        <LabeledInput label="Valid Until" type="date" value={draft.validity_date} onChange={v => setDraft(d => ({ ...d, validity_date: v }))} />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <button type="button" onClick={onSave} disabled={saving} style={btnPrimary}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
        </button>
        <button type="button" onClick={onCancel} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────── */

function StatTile({ label, value, color }) {
  return (
    <div style={tile}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={filterSelect}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function LabeledInput({ label, value, onChange, type = "text", disabled = false, mono = false }) {
  return (
    <div>
      <div style={formLabel}>{label}</div>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        autoComplete={type === "password" ? "new-password" : "off"}
        style={{ ...formInput, fontFamily: mono ? "monospace" : "inherit", background: disabled ? "#f8f9fa" : "#fff" }} />
    </div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={drawerPanel} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#2c3e50" }}>{title}</span>
          <button type="button" onClick={onClose} style={closeBtn}>✕</button>
        </div>
        <div style={{ padding: "20px 24px 24px", overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────── */

const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast        = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const statsGrid    = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 };
const tile         = { background: "#fff", borderRadius: 10, padding: "14px 18px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const filterRow    = { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" };
const searchInput  = { padding: "7px 12px", borderRadius: 8, border: "1px solid #dee2e6", fontSize: 13, flex: 1, minWidth: 200 };
const filterSelect = { padding: "7px 10px", borderRadius: 8, border: "1px solid #dee2e6", fontSize: 12, background: "#fff", cursor: "pointer" };
const tableCard    = { background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" };
const table        = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th           = { textAlign: "left", padding: "11px 14px", background: "#f8f9fa", borderBottom: "2px solid #dee2e6", fontWeight: 600, fontSize: 11, color: "#6c757d", textTransform: "uppercase" };
const td           = { padding: "10px 14px", borderBottom: "1px solid #f0f0f0", verticalAlign: "middle" };
const badge        = { display: "inline-block", padding: "3px 9px", borderRadius: 10, fontSize: 11, fontWeight: 600 };
const acctCountBtn = { padding: "3px 10px", borderRadius: 12, border: "1px solid #dee2e6", cursor: "pointer", fontSize: 12, fontWeight: 600 };
const btnPrimary   = { padding: "8px 18px", borderRadius: 8, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const btnSmall     = { padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 12 };
const btnAddAcct   = { padding: "5px 12px", borderRadius: 6, border: "1px dashed #007bff", background: "#f0f7ff", color: "#007bff", cursor: "pointer", fontSize: 11, fontWeight: 600 };
const addAcctForm  = { background: "#fff", borderRadius: 8, padding: "12px 14px", border: "1px solid #dee2e6", marginTop: 4 };
const smallInput   = { padding: "5px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: 12, width: "100%" };
const formLabel    = { fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 };
const formInput    = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%", boxSizing: "border-box" };
const overlay      = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1400, display: "flex", justifyContent: "flex-end" };
const drawerPanel  = { background: "#fff", width: "min(520px, 98vw)", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-4px 0 28px rgba(0,0,0,0.18)" };
const modalHeader  = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 };
const closeBtn     = { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999", lineHeight: 1 };
